import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * A single shimmer placeholder block. Size it via `className` (e.g. `h-4 w-24`).
 * The sweep animation lives in `index.css` (`.lz-skeleton`) and is disabled under
 * `prefers-reduced-motion`. Decorative — hidden from assistive tech.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('lz-skeleton rounded-sm', className)} />;
}

/** A few stacked text lines; the last is shortened so it reads as a paragraph. */
export function SkeletonText({
  lines = 3,
  className,
  lineClassName,
}: {
  lines?: number;
  className?: string;
  lineClassName?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-3', i === lines - 1 ? 'w-2/3' : 'w-full', lineClassName)}
        />
      ))}
    </div>
  );
}

/** A round placeholder (avatars, icon chips). `size` is a Tailwind h/w pair. */
export function SkeletonCircle({
  size = 'h-9 w-9',
  className,
}: {
  size?: string;
  className?: string;
}) {
  return <Skeleton className={cn('rounded-full', size, className)} />;
}

/** Wraps a skeleton region with the loading a11y role so screen readers announce
 * a busy state without reading the decorative blocks. */
function LoadingRegion({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div role="status" aria-label="Loading" className={className}>
      {children}
    </div>
  );
}

/**
 * Divided list rows inside a Card — the recurring "roster / feed" shape
 * (members, domains, activity, access lists). Each row shows an optional
 * avatar, two text lines, and `rightBlocks` filler cells (badges/stats/actions).
 */
export function SkeletonRows({
  rows = 5,
  avatar = false,
  rightBlocks = 1,
  className,
}: {
  rows?: number;
  avatar?: boolean;
  rightBlocks?: number;
  className?: string;
}) {
  return (
    <LoadingRegion
      className={cn(
        'divide-y divide-hairline rounded-md border border-hairline bg-surface',
        className,
      )}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-3 p-4">
          <div className="flex min-w-0 items-center gap-3">
            {avatar && <SkeletonCircle />}
            <div className="min-w-0 space-y-2">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
          <div className="flex flex-none items-center gap-4">
            {Array.from({ length: rightBlocks }).map((_, j) => (
              <Skeleton key={j} className="h-6 w-16" />
            ))}
          </div>
        </div>
      ))}
    </LoadingRegion>
  );
}

/** A grid of metric tiles (KPI row) on `surface-2`, matching the `Stat` tile. */
export function SkeletonStatGrid({ tiles = 4, className }: { tiles?: number; className?: string }) {
  return (
    <LoadingRegion className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>
      {Array.from({ length: tiles }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-3 rounded-md border border-hairline bg-surface-2 p-4"
        >
          <div className="flex items-center gap-3">
            <SkeletonCircle size="h-8 w-8" />
            <Skeleton className="h-7 w-16" />
          </div>
          <Skeleton className="h-2.5 w-20" />
        </div>
      ))}
    </LoadingRegion>
  );
}

/** A generic card body: an optional title line then paragraph lines. Use when a
 * box has no more specific shape. Pass `className` for padding/sizing. */
export function SkeletonCard({
  title = true,
  lines = 3,
  className,
}: {
  title?: boolean;
  lines?: number;
  className?: string;
}) {
  return (
    <LoadingRegion className={cn('rounded-md border border-hairline bg-surface p-5', className)}>
      {title && <Skeleton className="mb-4 h-4 w-40" />}
      <SkeletonText lines={lines} />
    </LoadingRegion>
  );
}

/** A fixed-height block for chart / sparkline regions still loading. */
export function SkeletonChart({
  className,
  height = 'h-40',
}: {
  className?: string;
  height?: string;
}) {
  return (
    <LoadingRegion className="w-full">
      <Skeleton className={cn('w-full rounded-md', height, className)} />
    </LoadingRegion>
  );
}
